import { useEffect, useRef, useState } from 'react';
import { DoorOpen, X } from 'lucide-react';
import { Avatar } from '@gmp/shared-ui';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { getPrefs, sound } from './prefs';
import { navigate } from './router';

/** Live party invites from friends: a card with Join / Dismiss, on every screen, until it expires (5 min). */
export function InviteCards() {
  const { invites, room } = usePartyState();
  const [, tick] = useState(0);
  const seen = useRef(new Set<string>());
  useEffect(() => {
    if (!invites.length) return;
    for (const i of invites) if (!seen.current.has(i.id)) {
      seen.current.add(i.id);
      sound.play('join');
    }
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [invites]);
  const now = party.now();
  const live = invites.filter((i) => i.expiresAt > now);
  if (!live.length) return null;
  return (
    <div className="fixed top-3 inset-x-0 z-[55] flex flex-col items-center gap-2 safe-pad safe-top pointer-events-none" aria-live="assertive">
      {live.slice(-2).map((i) => {
        const left = Math.max(0, Math.floor((i.expiresAt - now) / 1000));
        return (
          <div key={i.id} role="alertdialog" aria-label={`Party invite from ${i.from.displayName}`} className="pointer-events-auto card p-3 w-full max-w-md flex items-center gap-3 shadow-2xl border-2 border-[var(--accent)]" style={{ background: 'var(--color-ink-2)' }} data-testid="invite-card">
            <Avatar id={i.from.avatar} size={44} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-bold leading-tight"><span className="truncate">{i.from.displayName}</span> invited you</p>
              <p className="text-xs text-mute">{i.gameName}{room ? ' · you’ll leave this party’s screen' : ''} · {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}</p>
            </div>
            <button
              className="btn btn-sm"
              onClick={() => {
                const p = getPrefs();
                void party.answerInvite(i.id, true, p.nickname.trim() || undefined, p.avatar).then((r) => {
                  if (!r.ok) return party.notify(r.error ?? 'Could not join', 'bad');
                  // From any screen (Friends included) straight to the party, or its waiting room.
                  navigate({ name: 'party' });
                });
              }}
              data-testid="invite-join"
            >
              <DoorOpen size={16} aria-hidden /> Join
            </button>
            <button className="btn btn-sm btn-ghost" aria-label="Dismiss invite" onClick={() => void party.answerInvite(i.id, false)} data-testid="invite-dismiss"><X size={16} /></button>
          </div>
        );
      })}
    </div>
  );
}
